// v2.19.36 — Page Replay Timelapse `/replay/:order_ref`.
// PUBLIQUE (pas d'auth). Récupère les données via /api/v3/delivery/orders/:ref/replay,
// calcule OSRM si polyline absente, joue la course en 15 secondes avec puck cyan
// sur Mapbox Standard Dusk 3D, capture la vidéo via MediaRecorder pour partage WhatsApp.
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";
const REPLAY_DURATION_SEC = 15;
const TICK_MS = 200;

const _toRad = (d) => (d * Math.PI) / 180;
const _toDeg = (r) => (r * 180) / Math.PI;
const _bearingDeg = (a, b) => {
  const φ1 = _toRad(a[1]), φ2 = _toRad(b[1]);
  const Δλ = _toRad(b[0] - a[0]);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (_toDeg(Math.atan2(y, x)) + 360) % 360;
};
const _haversineM = (a, b) => {
  const R = 6371000;
  const dLat = _toRad(b[1] - a[1]);
  const dLon = _toRad(b[0] - a[0]);
  const lat1 = _toRad(a[1]), lat2 = _toRad(b[1]);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
};
const _polylineTotalLength = (coords) => {
  if (!coords || coords.length < 2) return 0;
  let total = 0;
  for (let i = 0; i < coords.length - 1; i++) total += _haversineM(coords[i], coords[i + 1]);
  return total;
};
const _advanceAlongPolyline = (coords, progressMeters) => {
  if (!coords || coords.length < 2) return null;
  let remaining = Math.max(0, progressMeters);
  for (let i = 0; i < coords.length - 1; i++) {
    const a = coords[i], b = coords[i + 1];
    const segLen = _haversineM(a, b);
    if (remaining <= segLen || i === coords.length - 2) {
      const t = segLen > 0 ? Math.min(1, remaining / segLen) : 0;
      const point = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
      const bearing = _bearingDeg(a, b);
      return { point, bearing, segmentIdx: i, finished: i === coords.length - 2 && remaining >= segLen };
    }
    remaining -= segLen;
  }
  const last = coords[coords.length - 1], prev = coords[coords.length - 2];
  return { point: last, bearing: _bearingDeg(prev, last), segmentIdx: coords.length - 2, finished: true };
};

async function _fetchOSRMPolyline(pickup, drop) {
  try {
    const url = `https://router.project-osrm.org/route/v1/driving/${pickup.lng},${pickup.lat};${drop.lng},${drop.lat}?overview=full&geometries=geojson`;
    const r = await fetch(url);
    const d = await r.json();
    if (d.code === "Ok" && d.routes?.[0]) {
      return d.routes[0].geometry.coordinates;
    }
  } catch (_) {}
  return null;
}

export default function ReplayPage() {
  const { order_ref } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [polyline, setPolyline] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [recording, setRecording] = useState(false);
  const [videoBlobUrl, setVideoBlobUrl] = useState(null);
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const puckMarkerRef = useRef(null);
  const tickRef = useRef(null);
  const progressRef = useRef(0);
  const recorderRef = useRef(null);
  const recordedChunksRef = useRef([]);

  // 1. Fetch replay data.
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/v3/delivery/orders/${encodeURIComponent(order_ref)}/replay`);
        const d = await r.json();
        if (r.ok && d.ok) {
          setData(d);
          if (d.polyline && d.polyline.length >= 2) {
            setPolyline(d.polyline);
          } else if (d.pickup && d.drop) {
            const coords = await _fetchOSRMPolyline(d.pickup, d.drop);
            if (coords && coords.length >= 2) setPolyline(coords);
            else setPolyline([[d.pickup.lng, d.pickup.lat], [d.drop.lng, d.drop.lat]]);
          }
        } else {
          setError(d.error || `HTTP ${r.status}`);
        }
      } catch (_) { setError("network_error"); }
    })();
  }, [order_ref]);

  // 2. Init Mapbox.
  useEffect(() => {
    if (!data || !polyline || !mapEl.current || mapRef.current || !MAPBOX_TOKEN) return;
    (async () => {
      const mod = await import("mapbox-gl");
      const mapboxgl = mod.default;
      await import("mapbox-gl/dist/mapbox-gl.css");
      mapboxgl.accessToken = MAPBOX_TOKEN;
      const map = new mapboxgl.Map({
        container: mapEl.current,
        style: "mapbox://styles/mapbox/standard",
        center: polyline[0],
        zoom: 16,
        pitch: 60,
        bearing: _bearingDeg(polyline[0], polyline[1]),
        antialias: true,
        preserveDrawingBuffer: true, // requis pour captureStream
      });
      mapRef.current = map;
      map.on("style.load", () => {
        try {
          map.setConfigProperty("basemap", "lightPreset", "dusk");
          map.setConfigProperty("basemap", "show3dObjects", true);
          map.setConfigProperty("basemap", "showRoadLabels", true);
          map.setConfigProperty("basemap", "showPlaceLabels", true);
        } catch (_) {}
      });
      map.on("load", () => {
        const geo = { type: "Feature", geometry: { type: "LineString", coordinates: polyline }, properties: {} };
        map.addSource("replay-route", { type: "geojson", data: geo });
        try {
          map.addLayer({ id: "replay-route-glow", type: "line", source: "replay-route", slot: "middle", layout: { "line-join": "round", "line-cap": "round" }, paint: { "line-color": "#00E5FF", "line-width": 18, "line-opacity": 0.25, "line-blur": 4 } });
          map.addLayer({ id: "replay-route-line", type: "line", source: "replay-route", slot: "middle", layout: { "line-join": "round", "line-cap": "round" }, paint: { "line-color": "#00E5FF", "line-width": 7, "line-opacity": 0.95, "line-blur": 0.5 } });
        } catch (_) {
          map.addLayer({ id: "replay-route-line", type: "line", source: "replay-route", layout: { "line-join": "round", "line-cap": "round" }, paint: { "line-color": "#00E5FF", "line-width": 7, "line-opacity": 0.95 } });
        }
        // Pickup marker.
        const pickEl = document.createElement("div"); pickEl.innerHTML = "📦"; pickEl.style.fontSize = "28px";
        new mapboxgl.Marker({ element: pickEl }).setLngLat([data.pickup.lng, data.pickup.lat]).addTo(map);
        // Drop marker.
        const dropEl = document.createElement("div"); dropEl.innerHTML = "🏁"; dropEl.style.fontSize = "28px";
        new mapboxgl.Marker({ element: dropEl }).setLngLat([data.drop.lng, data.drop.lat]).addTo(map);
        // Puck cyan with arrow.
        const puckEl = document.createElement("div");
        puckEl.innerHTML = `<div style="position:relative;width:38px;height:38px;"><div style="position:absolute;inset:0;border-radius:50%;background:radial-gradient(circle, rgba(0,229,255,0.95) 0%, rgba(0,229,255,0.45) 60%, transparent 75%);box-shadow:0 0 18px rgba(0,229,255,0.8);"></div><svg viewBox="0 0 24 24" style="position:absolute;inset:6px;width:26px;height:26px;"><path d="M12 2 L19 20 L12 16 L5 20 Z" fill="#0F172A" stroke="#FFFFFF" stroke-width="1.2" stroke-linejoin="round"/></svg></div>`;
        puckMarkerRef.current = new mapboxgl.Marker({ element: puckEl, rotationAlignment: "map", pitchAlignment: "map" }).setLngLat(polyline[0]).addTo(map);
        const initBearing = _bearingDeg(polyline[0], polyline[1]);
        try { puckMarkerRef.current.setRotation(initBearing); } catch (_) {}
      });
    })();
    return () => { try { mapRef.current?.remove(); mapRef.current = null; } catch (_) {} };
  }, [data, polyline]);

  // 3. Replay player.
  const stopPlayback = useCallback(() => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    setPlaying(false);
  }, []);

  const startPlayback = useCallback(() => {
    if (!polyline || !mapRef.current || playing) return;
    setPlaying(true);
    progressRef.current = 0;
    const totalLen = _polylineTotalLength(polyline);
    const speedMps = totalLen / REPLAY_DURATION_SEC; // arrive en 15s.
    const deltaPerTick = speedMps * (TICK_MS / 1000);
    const map = mapRef.current;
    tickRef.current = setInterval(() => {
      progressRef.current = Math.min(totalLen, progressRef.current + deltaPerTick);
      const adv = _advanceAlongPolyline(polyline, progressRef.current);
      if (!adv) return;
      try { puckMarkerRef.current?.setLngLat(adv.point); } catch (_) {}
      try { puckMarkerRef.current?.setRotation(adv.bearing); } catch (_) {}
      map.easeTo({ center: adv.point, bearing: adv.bearing, pitch: 60, zoom: 17.5, duration: 180 });
      if (adv.finished) {
        stopPlayback();
        toast.success("🎬 Replay terminé");
      }
    }, TICK_MS);
  }, [polyline, playing, stopPlayback]);

  // 4. Record video via MediaRecorder + captureStream.
  const startRecording = useCallback(() => {
    if (!mapRef.current || recording) return;
    try {
      const canvas = mapRef.current.getCanvas();
      const stream = canvas.captureStream(30);
      const mimes = ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm", "video/mp4"];
      const mime = mimes.find((m) => MediaRecorder.isTypeSupported(m)) || "";
      if (!mime) { toast.error("MediaRecorder non supporté sur ce navigateur"); return; }
      recordedChunksRef.current = [];
      const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 2500000 });
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: mime });
        const url = URL.createObjectURL(blob);
        setVideoBlobUrl(url);
        setRecording(false);
        toast.success("📹 Vidéo prête à télécharger");
      };
      recorderRef.current = rec;
      rec.start(200);
      setRecording(true);
      startPlayback();
      // Auto stop après duration + 1s tampon.
      setTimeout(() => { try { rec.state === "recording" && rec.stop(); } catch (_) {} }, (REPLAY_DURATION_SEC + 1) * 1000);
    } catch (e) {
      toast.error("Erreur enregistrement vidéo");
      setRecording(false);
    }
  }, [recording, startPlayback]);

  if (error) {
    return <div style={pageStyle}><div style={cardStyle}>
      <h1 style={{ color: "#F87171", fontSize: 22 }}>Replay indisponible</h1>
      <p style={{ color: "#94A3B8", marginTop: 10 }}>
        {error === "order_not_delivered" ? "Cette course n'est pas encore terminée." : "Course introuvable ou expirée."}
      </p>
      <Link to="/" style={ctaAltStyle} data-testid="replay-back-home">← Retour AYA AI</Link>
    </div></div>;
  }
  if (!data || !polyline) return <div style={pageStyle}><div style={{ color: "#94A3B8", padding: 24 }}>Chargement…</div></div>;

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const waText = `🎬 Voici le replay de ma livraison AYA ${data.order_ref} (${data.distance_km} km) : ${shareUrl}`;
  const waShareUrl = `https://wa.me/?text=${encodeURIComponent(waText)}`;

  return (
    <div style={pageStyle} data-testid="replay-page">
      <header style={headerStyle}>
        <Link to="/" style={{ color: "#FBBF24", textDecoration: "none", fontSize: 13 }} data-testid="replay-back">← AYA</Link>
        <div style={{ textAlign: "center", flex: 1 }}>
          <div style={{ fontSize: 11, color: "#FBBF24", fontWeight: 700, letterSpacing: 1 }}>REPLAY</div>
          <div style={{ fontFamily: "monospace", fontSize: 12, color: "#93C5FD" }} data-testid="replay-order-ref">{data.order_ref}</div>
        </div>
        <div style={{ width: 30 }} />
      </header>

      <div style={{ padding: "0 12px 12px", color: "#94A3B8", fontSize: 12, textAlign: "center" }}>
        📦 {data.commerce_name} → 🏁 {data.drop?.description || "Destination"} · {data.distance_km} km
      </div>

      {MAPBOX_TOKEN ? (
        <div ref={mapEl} data-testid="replay-map" style={{ width: "100%", height: "55vh", borderRadius: 0, overflow: "hidden", background: "#1F2937" }} />
      ) : (
        <div style={{ ...cardStyle, height: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>Token Mapbox manquant</div>
      )}

      <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
        <button onClick={startPlayback} disabled={playing} data-testid="replay-play-btn" style={{ ...ctaPrimaryStyle, opacity: playing ? 0.5 : 1 }}>
          {playing ? "▶ Lecture en cours…" : `▶️ Lancer le replay (${REPLAY_DURATION_SEC}s)`}
        </button>
        <button onClick={startRecording} disabled={recording} data-testid="replay-record-btn" style={{ ...ctaSecondStyle, opacity: recording ? 0.5 : 1 }}>
          {recording ? "📹 Enregistrement…" : "📹 Enregistrer en vidéo (WebM)"}
        </button>
        {videoBlobUrl && (
          <a href={videoBlobUrl} download={`AYA-replay-${data.order_ref}.webm`} data-testid="replay-download-btn" style={ctaSecondStyle}>
            ⬇️ Télécharger la vidéo
          </a>
        )}
        <a href={waShareUrl} target="_blank" rel="noopener noreferrer" data-testid="replay-share-wa" style={waBtnStyle}>
          📲 Partager le replay sur WhatsApp
        </a>
      </div>
      <div style={{ textAlign: "center", color: "#64748b", fontSize: 11, padding: 12 }}>Livré via AYA AI 🇨🇮</div>
    </div>
  );
}

const pageStyle = { minHeight: "100vh", background: "radial-gradient(ellipse at top, #14223D 0%, #0A0F1F 70%)", color: "#E8ECF7", fontFamily: "system-ui, sans-serif" };
const headerStyle = { display: "flex", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.08)" };
const cardStyle = { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 12, padding: 18, margin: 14 };
const ctaPrimaryStyle = { background: "linear-gradient(135deg, #16A34A, #22C55E)", color: "#0F172A", border: "none", padding: "14px 22px", borderRadius: 12, fontWeight: 800, fontSize: 15, cursor: "pointer", boxShadow: "0 4px 14px rgba(34,197,94,0.4)", textAlign: "center", textDecoration: "none", display: "block" };
const ctaSecondStyle = { background: "rgba(255,255,255,0.06)", color: "#E8ECF7", border: "1px solid rgba(255,255,255,0.12)", padding: "12px 18px", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer", textAlign: "center", textDecoration: "none", display: "block" };
const ctaAltStyle = { display: "inline-block", marginTop: 12, color: "#E8ECF7", textDecoration: "none", fontSize: 13, padding: "10px 18px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.12)" };
const waBtnStyle = { background: "#25D366", color: "#fff", textDecoration: "none", fontWeight: 700, fontSize: 14, padding: "13px 22px", borderRadius: 12, boxShadow: "0 4px 14px rgba(37,211,102,0.4)", textAlign: "center", display: "block" };
